import {
  isPremiumFeatureAllowed,
  LimitCheckFunctionParams,
} from '@griply/utils';
import { isBefore, isSameDay } from 'date-fns';
import { useMemo } from 'react';
import { useToday } from 'shared/contexts/today';
import { LimitKeys } from 'shared/types/limit-keys';

import { useUser } from './use-user';

export const usePremiumFeatureAllowed = <LimitKey extends LimitKeys>(
  limitKey: LimitKey,
  options: Omit<LimitCheckFunctionParams[LimitKey], 'user' | 'today'>,
) => {
  const today = useToday();
  const user = useUser();

  return useMemo(() => {
    if (!user) {
      return {
        limit: 0,
        enforced: true,
        enforcedAt: today,
        allowed: false,
      };
    }

    const limitEntry = user.limits?.[limitKey];
    if (!limitEntry) {
      return {
        limit: 0,
        enforced: false,
        enforcedAt: undefined,
        allowed: true,
      };
    }

    const enforceDate = limitEntry.enforcedAt;

    return {
      limit: limitEntry.limit,
      enforced: enforceDate
        ? isBefore(enforceDate, today) || isSameDay(enforceDate, today)
        : false,
      enforcedAt: enforceDate,
      allowed: isPremiumFeatureAllowed(limitKey, {
        ...options,
        user,
        today,
      } as LimitCheckFunctionParams[LimitKey]),
    };
  }, [limitKey, options, today, user]);
};
